// ffmpeg -i unify.mov -an -vcodec h264 -acodec mp2 unify.mp4
// ffmpeg -i unify.mp4 -an -filter:v "crop=iw:ih-248:0:248" unify_crop.mp4
// ffmpeg -i unify_crop.mp4 -vf "scale=2080:-2" -c:a copy output.mp4
// ffmpeg -i output.mp4 -vf "select=eq(n\,0)" -q:v 3 unify.jpg

import React, { Component } from "react";

import Menu from "./menu";

import LogoSVG from "./images/recollect/logo.svg";
import extensionMP4 from "./images/recollect/extension.mp4";
import extensionJPG from "./images/recollect/extension.jpg";
import recallMP4 from "./images/recollect/recall.mp4";
import recallJPG from "./images/recollect/recall.jpg";
import playgroundMP4 from "./images/recollect/playground.mp4";
import playgroundJPG from "./images/recollect/playground.jpg";
import unifyMP4 from "./images/recollect/unify.mp4";
import unifyJPG from "./images/recollect/unify.jpg";

import "./css/recollect.css";

class Recollect extends Component {
  render() {
    return (
      <div className="recollect-page">
        <Menu type="projects" />
        <div className="intro section-wrapper">
          <div className="intro-background" />
          <div className="section">
            <div className="content">
              <img src={LogoSVG} className="logo" alt="re:collect" />
              <div className="intro-quote">
                <p>
                  A digital Zen master would say that if everything is starred, <em>nothing is</em>.
                </p>
                <p>
                  We’ve optimized the system for getting things in, but how do we get something good out? How can we
                  make meaningful connections between all of this stuff, and make constellations out of all these stars?
                </p>
                <p> — Frank Chimero</p>
              </div>
            </div>
          </div>
        </div>

        <div className="section-wrapper">
          <div className="section context">
            <div className="column">
              <h1>Tools for the second pass</h1>
              <h2>Opportunity</h2>
              <p>
                <em>Does collecting things matter if we don’t revisit them?</em>
              </p>
              <p>
                Frank Chimero's{" "}
                <a
                  href="https://2011.dconstruct.org/conference/frank-chimero"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  dconstruct 2011 talk
                </a>{" "}
                would whisper—sometimes yell—from the back of my head for over a decade.
              </p>
              <p>
                It's what I thought about every time my phone got just a little smarter every year, every time I
                re-stumbled upon an article or talk that profoundly shaped my worldview, so much so that I forgot the
                source. And it's all I could think about the first time I met{" "}
                <a href="https://x.com/AliceAlbrecht" target="_blank" rel="noopener noreferrer">
                  Alice
                </a>{" "}
                for coffee to discuss her new venture re:collect.
              </p>
              <h2>Introduction</h2>
              <p>
                2021 was still early enough that I didn't forsee the generative AI storm brewing but I did know machine
                learning had to play a significant role in solving the rediscovery problem. I build tools
                professionally, but I lacked a mental model for how machines learn. Wanting to remedy this, I accepted
                Alice's challenge and joined the founding team. As the only designer and front-end engineer on the team
                I owned the web application and browser extension presented below.
              </p>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Attention as curation</h2>
              <p>
                The browser extension watches over your shoulder and automatically collects content you pay attention
                to. Add additional context through notes and highlights to engage with content on a deeper level and
                leave a trail for your future self.
              </p>
              <p className="technically">
                Technically speaking: this was my first experience writing a browser extension and it was a very
                positive one. The UI layer is a modern React application that shares a lot of code with the main web
                client.
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto" poster={extensionJPG}>
                <source src={extensionMP4} type="video/mp4" />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Unify information silos</h2>
              <p>
                The ambition was to deeply index all the content you consume and create, making it effortlessly
                rediscoverable. It currently supports web articles, PDFs, YouTube transcripts, Tweets, and automatically
                pulls from sources like RSS, Twitter bookmarks, Readwise, Apple Notes, and Google Drive documents, with
                more integrations planned.
              </p>
              <p className="technically">
                Technically speaking: due to the small team size I also designed and maintained a lot of the Python API
                layer the web clients directly interfaced with and was involved in the system architecture decisions as
                it evolved.
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto" poster={unifyJPG}>
                <source src={unifyMP4} type="video/mp4" />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Explore what you've read and written</h2>
              <p>
                Recall brings back content connected to what you're thinking about as collections of related cards. Pick
                between tranditional search when looking for something specific or take the scenic route and explore
                related material, hopefully rediscovering something even more interesting along the way!
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto" poster={recallJPG}>
                <source src={recallMP4} type="video/mp4" />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider">
          <div className="section">
            <div className="column">
              <h2>Play with ideas</h2>
              <p>
                Playgrounds allow you to think out loud on an infinite canvas. Keep and make notes on highlights as you
                Recall what you already know about a particular topic. Spread it out, and rearrange the pieces as you
                explore the connections. Finally, impose order, hierarchy, and linearity as your ideas clarify.
              </p>
              <p className="technically">
                Technically speaking: there were many fun challenges in building Playgrounds, from the infinite canvas
                to the card layout engine, rich text editors (Slate.js), multi-level undo system and sync engine to name
                a few.
              </p>
            </div>
            <div className="image-wrapper video">
              <video autoPlay loop muted playsInline preload="auto" poster={playgroundJPG}>
                <source src={playgroundMP4} type="video/mp4" />
                Your browser doesn't support HTML5 video tag.
              </video>
            </div>
          </div>
        </div>

        <div className="section-wrapper has-divider conclusion">
          <div className="section">
            <div className="column">
              <h2>Looking ahead</h2>
              <p>
                I'm grateful for the opportunity to scratch a lot of personal itches and toy with ideas that took up
                space in my head rent free for over a decade in this role. While the product has since been shut down,
                the rest of the team has{" "}
                <a
                  href="https://about.smartnews.com/en/2024/09/26/from-recollect-founder-to-smartnews-reflections-on-why-what-we-consume-shapes-our-thinking/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  joined SmartNews
                </a>{" "}
                and continues to innovate in new directions.
              </p>
            </div>
            <div className="column">
              <p>
                <a href="#top" className="to-top">
                  Back to top
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Recollect;



// WEBPACK FOOTER //
// ./src/recollect.js